import React, { useState, useCallback } from "react";
import type { Machine } from "../services/BackendDataService";
import Tabs from "./Tabs";
import EnergyConditionMonitoring from "./EnergyConditionMonitoring";
import ServoConditionMonitoring from "./ServoConditionMonitoring";
import MotorConditionMonitoring from "./MotorConditionMonitoring";
import DateRangeBar, {
  type RangePreset,
  type DateRangeState,
  initialDateRangeState,
  presetToStartTime,
  presetToSampleIntervalMs,
} from "./ConditionMonitoringDateRange";
import { styles } from "../styles";
import { useTheme } from "../providers/ThemeProvider";

interface ConditionMonitoringProps {
  machineId?: string;
  machineName?: string;
  machineData?: Machine | null;
}

const ConditionMonitoring: React.FC<ConditionMonitoringProps> = ({
  machineData,
}) => {
  const [activeTab, setActiveTab] = useState<string>("Energy");
  const [rangeState, setRangeState] = useState<DateRangeState>(
    initialDateRangeState,
  );
  // When resuming from pause, replay from this time instead of the preset start
  const [catchupStartTime, setCatchupStartTime] = useState<
    string | undefined
  >();
  const { currentTheme } = useTheme();

  const tabs = ["Energy", "Servo", "Motor"];

  const handleRangeChange = useCallback(
    (preset: RangePreset, customDate: string) => {
      setCatchupStartTime(undefined);
      // Compute startTime using the stream's newest OPC-UA source timestamp as reference
      setRangeState((prev) => {
        const startTime = presetToStartTime(
          preset,
          customDate,
          prev.newestDate,
        );
        return {
          ...prev,
          preset,
          customDate,
          startTime,
          replaying: false,
          replayProgress: 0,
        };
      });
    },
    [],
  );

  const handleStreamInfo = useCallback(
    (firstMessageTime: string | null, lastMessageTime: string | null) => {
      setRangeState((prev) => {
        const updates: Partial<DateRangeState> = {};
        if (firstMessageTime) updates.oldestDate = firstMessageTime;
        if (lastMessageTime) updates.newestDate = lastMessageTime;
        // Recompute startTime now that we know the stream's actual time range
        if (
          lastMessageTime &&
          prev.preset !== "live" &&
          prev.preset !== "custom"
        ) {
          updates.startTime = presetToStartTime(
            prev.preset,
            prev.customDate,
            lastMessageTime,
          );
        }
        return { ...prev, ...updates };
      });
    },
    [],
  );

  const handlePauseToggle = useCallback(() => {
    setRangeState((prev) => {
      if (!prev.paused) {
        // Pausing — record the moment
        return { ...prev, paused: true, pausedAt: new Date().toISOString() };
      } else {
        // Resuming — trigger catchup replay from pausedAt, then go live
        if (prev.pausedAt) setCatchupStartTime(prev.pausedAt);
        return { ...prev, paused: false, pausedAt: null };
      }
    });
  }, []);

  // Use catchupStartTime when resuming, otherwise the preset-based startTime stored in state
  const startTime = catchupStartTime ?? rangeState.startTime;
  // Sample interval scales with range: wider range → fewer points → faster load
  const sampleIntervalMs = presetToSampleIntervalMs(
    rangeState.preset,
    rangeState.customDate,
  );

  return (
    <main
      className={styles.overview.container}
      style={{
        backgroundColor: currentTheme.isDark
          ? "var(--bg-secondary)"
          : "#f3f4f6",
        transition: "background-color 0.3s ease",
      }}
    >
      {/* Date range selector — shared across all tabs */}
      <div className="mt-4">
        <DateRangeBar
          state={rangeState}
          onChange={handleRangeChange}
          onPauseToggle={handlePauseToggle}
        />
      </div>

      <div className="mt-4">
        <Tabs tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>

      <div className={styles.overview.content.container}>
        {activeTab === "Energy" && (
          <EnergyConditionMonitoring
            machineData={machineData}
            startTime={startTime}
            paused={rangeState.paused}
          />
        )}
        {activeTab === "Servo" && (
          <ServoConditionMonitoring
            machineData={machineData}
            startTime={startTime}
            paused={rangeState.paused}
            onStreamInfo={handleStreamInfo}
            sampleIntervalMs={sampleIntervalMs}
          />
        )}
        {activeTab === "Motor" && (
          <MotorConditionMonitoring
            machineData={machineData}
            startTime={startTime}
            paused={rangeState.paused}
            onStreamInfo={handleStreamInfo}
            sampleIntervalMs={sampleIntervalMs}
          />
        )}
      </div>
    </main>
  );
};

export default ConditionMonitoring;
